import { useCallback, useEffect, useState } from 'react';
import { Button, CenterScreenModal, RowSpacer } from '../atoms'
import {
  useAddPicturesFromIncidentFormModalContext,
  useErrorHandlerContext,
  useIncidentDetailContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'
import { Col, Row } from 'react-grid-system';
import {
  FileOriginOption,
  FileTypeOption,
  useGetFileUploadUrlsMutation,
  useUpdateIncidentMutation,
} from '@/graphql/generated';
import { ImageUploader, VideoUploader } from '../molecules';

const textsData = {
  en: {
    title: 'Add New Evidence',
    cancelButton: 'Cancel',
    confirmButton: 'Save',
    confirmation: {
      create: {
        title: 'Evidencias subidas',
        description: 'Evidencias subidas con éxito',
      }
    }
  },
  es: {
    title: 'Agregar Evidencias',
    cancelButton: 'Cancelar',
    confirmButton: 'Guardar',
    confirmation: {
      create: {
        title: 'Evidencias subidas',
        description: 'Evidencias subidas con éxito',
      }
    }
  }
}

export const AddPicturesFromIncidentFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [isLoading, setIsLoading] = useState(false);
  const [picturesState, setPictures] = useState<File[]>([])
  const [videosState, setVideos] = useState<File[]>([])

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  useEffect(() => {
    if (picturesState.length > 0 || videosState.length > 0) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [picturesState, videosState])
  const [getUploadUrls] = useGetFileUploadUrlsMutation()

  const [updateIncident] = useUpdateIncidentMutation()
  const {isOpen, closeModal} = useAddPicturesFromIncidentFormModalContext()

  const {incident, setIncident} = useIncidentDetailContext();

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const onConfirmHandle = useCallback(async () => {
    if (picturesState.length === 0 && videosState.length === 0) return

    const onSuccesHandle = () => {
      confirmDialogModalOpen({
        title: texts.confirmation.create.title,
        description: texts.confirmation.create.description,
      });
      closeModal();
      setIncident(undefined);
    }

    try {
      let fileUploadsIds: string[] = [];
      setIsLoading(true);
      const pictures = picturesState;
      const videos = videosState;

      if (pictures.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: pictures.map((picture) => ({
              contentType: picture.type,
              fileExtension: 'webp',
              fileTypeOption: FileTypeOption.Picture,
              fileOriginOption: FileOriginOption.Incident,
              fileSize: picture.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== pictures.length) {
          throw new Error("No se pudieron obtener las URLs para subir los archivos.");
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          const uploadUrl = uploadData.uploadUrl ?? '';
          return fetch(uploadUrl, {
            method: 'PUT',
            body: pictures[index],
            headers: { 'Content-Type': pictures[index].type },
          });
        });

        await Promise.all(uploadPromises);
        fileUploadsIds = uploadDataList.map(uploadData => uploadData.id);
      }

      if (videos.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: videos.map((video) => ({
              contentType: 'video/mp4',
              fileExtension: 'mp4',
              fileTypeOption: FileTypeOption.Video,
              fileOriginOption: FileOriginOption.Incident,
              fileSize: video.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== videos.length) {
          throw new Error('No se pudieron obtener las URLs para subir los videos.');
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          const uploadUrl = uploadData.uploadUrl ?? '';
          return fetch(uploadUrl, {
            method: 'PUT',
            body: videos[index],
            headers: { 'Content-Type': 'video/mp4' },
          });
        });

        await Promise.all(uploadPromises);
        fileUploadsIds = fileUploadsIds.concat(uploadDataList.map(uploadData => uploadData.id));
      }

      await updateIncident({ variables: {
        incidentId: incident?.id ?? '',
        uploadedFilesIds: fileUploadsIds,
      }})
      // Aca va la mutacion de update de incidenteenviando los nuevos URLS
      /* await createIncidentReport({ variables: {
        incidentId: incident?.id ?? '',
        input: {
          subject: reportDataRef.current.subject,
          note: reportDataRef.current.note,
          fileUploadsIds,
        },
      }}); */
      onSuccesHandle();
    } catch (error: any) {
      errorHandle(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    picturesState,
    updateIncident,
    getUploadUrls,
    incident?.id,
    setIncident,
    closeModal,
    confirmDialogModalOpen,
    errorHandle,
    texts.confirmation.create.title,
    texts.confirmation.create.description
  ]);
  
  return (
    <CenterScreenModal
    isOpen={isOpen}
    onClose={closeModal}
    >
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
      </Row>
      <RowSpacer height={2} />
      <ImageUploader onUploadReady={setPictures}/>
      <VideoUploader onUploadReady={setVideos}/>
      <RowSpacer height={3} />
      <Row justify="center" gutterWidth={32}>
        <Col xs="content">
          <Button secondary onClick={() => closeModal()} disabled={isLoading}>
            {texts.cancelButton}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => onConfirmHandle()}
            isLoading={isLoading}
            disabled={isButtonDisabled || isLoading}
          >
            {texts.confirmButton}
          </Button>
        </Col>
      </Row> 
    </CenterScreenModal>
  )
}
